import z from 'zod';

const ErrorDetailSchema = z.object({
  location: z.string().optional(),
  message: z.string().optional(),
  value: z.unknown().optional(),
});

export const ProblemDetailsSchema = z.object({
  type: z.string().optional(),
  title: z.string().optional(),
  status: z.number().optional(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  errors: z.array(ErrorDetailSchema).nullable().optional(),
});

type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;

/** Fills in the route in place (preserving the original stack) once it's known — for errors like
 *  TimeoutError/NetworkError that are thrown before the interceptor can supply their route. */
export function setErrorRoute<T extends { route?: string }>(error: T, route: string): T {
  error.route = route;
  return error;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';

  readonly sentryFingerprint?: string[];

  constructor(
    public readonly status: number,
    public readonly data: ProblemDetails,
    public readonly headers: Headers,
    public readonly url: string,
    public readonly method: string,
    /** Un-interpolated OpenAPI path template, e.g. `/orders/{orderId}`. */
    public readonly route?: string,
  ) {
    super(data.detail ?? data.title ?? `HTTP ${status}`);
    Object.setPrototypeOf(this, ApiError.prototype);
    if (route) {
      // Route template, not stack or message: those split one endpoint per path-param value.
      this.sentryFingerprint = [this.name, method, route, String(status)];
    }
  }
}

/** Why a timeout says nothing about the server: the client was offline, or the tab was frozen. */
export type TimeoutClientCause = 'offline' | 'backgrounded';

export class TimeoutError extends Error {
  override readonly name = 'TimeoutError';

  /** One issue per error class, route excluded: see ADR-013 §Grouping. */
  readonly sentryFingerprint = [this.name];

  /** Not known at throw time — filled in later via `setErrorRoute` once the interceptor has it. */
  route?: string;

  constructor(
    public readonly timeoutMs: number,
    public readonly url: string,
    public readonly method: string,
    public readonly clientCause?: TimeoutClientCause,
  ) {
    // URL-free: this is the Sentry issue title, shared by every route under one fingerprint.
    super(`Request timed out after ${timeoutMs}ms`);
    Object.setPrototypeOf(this, TimeoutError.prototype);
  }

  /** A timeout the client explains itself is shown to the user, never reported. */
  get sentryIgnore(): boolean {
    return this.clientCause !== undefined;
  }
}

/**
 * Thrown when a request never reached the server (offline, DNS, connection reset,
 * CORS). `fetch` rejects with a bare `TypeError` there, indistinguishable from a
 * programming bug of the same class, so callers had to guess.
 */
export class NetworkError extends Error {
  override readonly name = 'NetworkError';

  /** A request that never left the client says nothing about the server. */
  readonly sentryIgnore = true;

  /** Unreachable while `sentryIgnore` holds. Kept so relaxing that cannot silently restore
   *  per-stack grouping. */
  readonly sentryFingerprint = [this.name];

  constructor(
    cause: TypeError,
    public readonly url: string,
    public readonly method: string,
  ) {
    // Verbatim: the browser's own wording is the most useful thing this error can say.
    super(cause.message, { cause });
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * Thrown when a client-driven session refresh could not complete because the
 * identity provider was briefly unreachable (a transient failure, not a
 * rejection). The session is preserved: consumers should surface a retryable
 * error rather than logging the user out — a user-driven refetch will retry.
 */
export class TransientAuthError extends Error {
  override readonly name = 'TransientAuthError';

  /** The session survived: a blip to retry, not a crash to report. */
  readonly sentryIgnore = true;

  constructor() {
    super('Session refresh temporarily unavailable');
    Object.setPrototypeOf(this, TransientAuthError.prototype);
  }
}

/** True when the error is a 401 ApiError — used to short-circuit consumers
 *  that delegate the response to a registered unauthorized handler. */
export function isAuthRedirectError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 401;
}

/** True for a transient session-refresh failure (IdP briefly unreachable). The
 *  session is preserved; consumers should retry rather than log out or surface a
 *  hard error. */
export function isTransientAuthError(error: unknown): error is TransientAuthError {
  return error instanceof TransientAuthError;
}

/** True for a request that never reached the server. */
export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

/** True when the error is a 409 ApiError — used by consumers that surface a duplicate-submission
 *  state as a soft success rather than a destructive failure. */
export function isConflictError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 409;
}

/** True when the error is a 422 ApiError. Huma refuses the input before the handler runs, so the
 *  `type` is `about:blank` and status is the only discriminator left to match on. */
export function isValidationError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 422;
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/** Error details returned by the BFF reCAPTCHA middleware. */
const RECAPTCHA_DETAILS = new Set(['recaptcha_required', 'recaptcha_failed']);

/** True when the ApiError was produced by the reCAPTCHA middleware (400 missing token / 403 verification failed). */
export function isRecaptchaError(err: ApiError): boolean {
  return (err.status === 400 || err.status === 403) && RECAPTCHA_DETAILS.has(err.data.detail ?? '');
}
